/*
  Исполнительное производство — §3.6.

  Экран отвечает на один вопрос: что стало с приказом после суда. Именно здесь
  теряются деньги - приказ сам по себе ничего не взыскивает, а срок его
  предъявления идёт молча три года.

  🔴 Переделан 22.09.2026 по решению владельца, следом за радаром и тем же
  устройством: плитки с суммами, переключатель по состоянию и одна таблица.
  Два соседних раздела кабинета устроены одинаково, и человеку не приходится
  учиться второму экрану. Действия ушли в окно - форма из трёх полей внутри
  строки таблицы либо расталкивает её, либо не помещается.

  Три величины наверху, потому что за ними стоят деньги: присуждено, взыскано и
  то, что **получено и не предъявлено**. Последнее - не строка списка: получить
  приказ и не предъявить его месяц можно только по забывчивости.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { can, canAssemble } from '../components/access.js'
import { Empty, Notice, Switch } from '../components/Common.jsx'
import EnforcementActions from '../components/EnforcementActions.jsx'
import EnforcementList from '../components/EnforcementList.jsx'
import UndocumentedOrders from '../components/UndocumentedOrders.jsx'
import useCourtSections from '../components/useCourtSections.js'
import { date, documents as documentsWord, money } from '../api/format.js'

const ALL = 'all'
const IDLE = 'idle'
const WORKING = 'working'

/*
  С 21.10.2026 страницы документов и отбор части реестра делает сервер (требование
  владельца: «пагинация серверная»). Суммы плиток и числа на переключателе приходят
  вместе со страницей и считаются по ВСЕМУ реестру: по одной странице «Лежит без движения 2»
  было бы неверным числом.
*/
export default function EnforcementPage({ profile }) {
  const [portfolio, setPortfolio] = useState(null)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [shown, setShown] = useState(ALL)
  const [acting, setActing] = useState(null)
  const [busyId, setBusyId] = useState(null)
  // Растёт после события или правки суда: список перечитывает текущую страницу и суммы.
  const [version, setVersion] = useState(0)
  // Участки грузит страница, а не окно: оно открывается по нажатию, и запрос в
  // этот момент задержал бы появление формы.
  const { sections, error: sectionsError } = useCourtSections(
    profile?.permissions?.includes('build_documents') ?? false,
  )

  const canAct = can(profile, 'manage_stages')
  // Заявление об индексации - сборка документов: ярус «Под ключ» её закрывает (строка 127),
  // кнопка не показывается вовсе, а отметка события остаётся.
  const canIndex = canAssemble(profile)

  function done(text, kind) {
    setActing(null)
    setNotice({ text, kind })
    setVersion((value) => value + 1)
  }

  async function buildIndexation(item) {
    setBusyId(item.id)
    setNotice(null)
    setError(null)
    try {
      const built = await api.buildIndexation(item.id)
      setNotice({
        text: `Готово: ${documentsWord(built.length)}. Они в карточке счёта, в списке документов.`,
        kind: 'ok',
      })
    } catch (failure) {
      setError(failure)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <>
      <div className="head">
        <div className="head__title">Исполнительное производство</div>
        {portfolio ? <div className="head__note">на {date(portfolio.as_of)}</div> : null}
      </div>

      {/* Сбой действия не стирает реестр (аудит 26.10.2026, причина 1): плашка
          встаёт над списком, а список и суммы остаются на месте. */}
      <Notice error={error} wide />
      {notice ? <Notice kind={notice.kind}>{notice.text}</Notice> : null}

      {/* Над заглушкой «документов нет», а не внутри реестра: именно при пустом реестре
          счёт с полученным, но не заведённым приказом и теряется (строка 304). */}
      {portfolio ? (
        <UndocumentedOrders
          count={portfolio.orders_without_document}
          accounts={portfolio.orders_without_document_accounts}
        />
      ) : null}

      {portfolio && portfolio.documents_count === 0 ? (
        <Empty title="Исполнительных документов нет">
          Здесь появятся приказы и листы после суда: где документ находится, предъявлен
          ли он и сколько осталось до конца срока предъявления. Приказ заводится на
          карточке счёта, по которому он вынесен, - блок «Приказ получен».
        </Empty>
      ) : (
        <>
          {portfolio ? <Totals portfolio={portfolio} /> : null}

          {portfolio?.burning_count ? (
            <Notice kind="warn" wide>
              <div className="notice__what">
                Документов с горящим сроком предъявления: {portfolio.burning_count}.
              </div>
              <div className="notice__do">Срок предъявления идёт молча. Проверьте их первыми.</div>
            </Notice>
          ) : null}

          {/* Переключатель отбирает, а не сортирует: порядок внутри отбора
              остаётся серверным. Счётчик стоит в подписи - «Лежит без движения
              2» отвечает на вопрос до нажатия. */}
          {portfolio ? (
            <div className="row">
              <Switch
                value={shown}
                onChange={setShown}
                options={[
                  { value: ALL, title: `Всё ${portfolio.documents_count}` },
                  { value: IDLE, title: `Лежит без движения ${portfolio.idle_count}` },
                  { value: WORKING, title: `В работе ${portfolio.working_count}` },
                ]}
              />
            </div>
          ) : null}

          <div className="card">
            {/* Другая часть реестра - другой список: ключ начинает чтение с первой страницы. */}
            <EnforcementList
              key={shown}
              view={shown}
              version={version}
              onPortfolio={setPortfolio}
              canAct={canAct}
              onEvent={setActing}
              onIndexation={canIndex ? buildIndexation : undefined}
              busyId={busyId}
            />
          </div>
        </>
      )}

      {acting ? (
        <EnforcementActions
          item={acting}
          sections={sections}
          sectionsError={sectionsError}
          onDone={done}
          onClose={() => setActing(null)}
        />
      ) : null}
    </>
  )
}

/*
  Три суммы рядом. Красным - только то, что лежит без предъявления: цвет тревоги
  означает потерю денег во времени и ничего другого (§4.5.3). Присуждённое и
  взысканное - не потеря, и красить их нельзя.
*/
function Totals({ portfolio }) {
  const idleCount = portfolio.idle_count
  return (
    <div className="grid grid--even">
      <div className="tile tile--plain">
        <div className="money__label">Присуждено</div>
        <div className="money__value">{money(portfolio.awarded_total)}</div>
        <div className="money__note">
          {documentsWord(portfolio.documents_count)} в реестре
        </div>
      </div>
      <div className="tile tile--good">
        <div className="money__label">Взыскано</div>
        <div className="money__value">{money(portfolio.recovered_total)}</div>
        <div className="money__note">по тем же документам</div>
      </div>
      <div className="tile tile--loss">
        <div className="money__label">Лежит без предъявления</div>
        <div className="money__value money__value--burn">{money(portfolio.idle_total)}</div>
        <div className="money__note">
          {documentsWord(idleCount)} · получено и не предъявлено: эти деньги не взыскиваются
        </div>
      </div>
    </div>
  )
}
